import { useState, useEffect, useCallback } from 'react'
import './FrameThumbnails.css'

function FrameThumbnails({ frames }) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [previewIndex, setPreviewIndex] = useState(null)

  const frameCount = frames?.length ?? 0
  const isPreviewOpen = previewIndex !== null

  const closePreview = useCallback(() => {
    setPreviewIndex(null)
  }, [])

  const goToPrevious = useCallback(() => {
    if (frameCount === 0) return
    setPreviewIndex((prev) => (prev > 0 ? prev - 1 : frameCount - 1))
  }, [frameCount])

  const goToNext = useCallback(() => {
    if (frameCount === 0) return
    setPreviewIndex((prev) => (prev < frameCount - 1 ? prev + 1 : 0))
  }, [frameCount])

  // Keyboard navigation
  useEffect(() => {
    if (!isPreviewOpen) return

    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'Escape':
          closePreview()
          break
        case 'ArrowLeft':
          goToPrevious()
          break
        case 'ArrowRight':
          goToNext()
          break
        default:
          break
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    document.body.style.overflow = 'hidden' // Prevent scrolling when modal open

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [isPreviewOpen, closePreview, goToPrevious, goToNext])

  if (!frames || frames.length === 0) {
    return null
  }

  return (
    <div className="frame-thumbnails">
      <div 
        className="frame-thumbnails-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h3>
          <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>▶</span>
          Extracted Frames ({frames.length})
        </h3>
        <p>Click a frame to view full size</p>
      </div>
      
      {isExpanded && (
        <div className="frame-thumbnails-grid">
          {frames.map((frame, index) => (
            <div 
              key={index} 
              className="frame-thumbnail"
              onClick={() => setPreviewIndex(index)}
            >
              <img 
                src={frame} 
                alt={`Frame ${index + 1}`}
                loading="lazy"
              />
              <span className="frame-number">Frame {index + 1}</span>
            </div>
          ))}
        </div>
      )}

      {/* Full Preview Modal */}
      {isPreviewOpen && (
        <div className="frame-preview-overlay" onClick={closePreview}>
          <div className="frame-preview-content" onClick={(e) => e.stopPropagation()}>
            {/* Close button */}
            <button className="frame-preview-close" onClick={closePreview} aria-label="Close preview">
              ×
            </button>

            {/* Previous button */}
            <button 
              className="frame-preview-nav frame-preview-prev" 
              onClick={goToPrevious}
              aria-label="Previous frame"
            >
              ‹
            </button>

            {/* Image */}
            <div className="frame-preview-image-container">
              <img 
                src={frames[previewIndex]} 
                alt={`Frame ${previewIndex + 1} preview`}
                className="frame-preview-image"
              />
              <div className="frame-preview-info">
                Frame {previewIndex + 1} of {frames.length}
              </div>
            </div>

            {/* Next button */}
            <button 
              className="frame-preview-nav frame-preview-next" 
              onClick={goToNext}
              aria-label="Next frame"
            >
              ›
            </button>
          </div>

          {/* Thumbnail strip */}
          <div className="frame-preview-strip">
            {frames.map((frame, index) => (
              <button
                key={index}
                className={`frame-preview-strip-item ${index === previewIndex ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation()
                  setPreviewIndex(index)
                }}
              >
                <img src={frame} alt={`Frame ${index + 1}`} />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default FrameThumbnails

